import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import config from "../config";
import BuzzShell from "./BuzzShell";

// Ό,τι διάλεξε ο χρήστης στον υπολογιστή τιμής της landing (τραπέζια/περίοδος).
// Χωρίς αποθηκευμένη επιλογή: τα ελάχιστα 5 τραπέζια και ετήσια χρέωση.
function readSignupPref() {
  try {
    const p = JSON.parse(localStorage.getItem("buzz_signup_pref") || "null");
    return {
      tables: Number.isFinite(p?.tables) && p.tables > 0 ? p.tables : 5,
      period: p?.period === "monthly" ? "monthly" : "yearly",
    };
  } catch { return { tables: 5, period: "yearly" }; }
}

export default function BuzzBilling() {
  const { t } = useTranslation();
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState(false);
  const prefRef = useRef(readSignupPref());
  const appliedPrefRef = useRef(false);
  // Πλάνο τραπεζιών πριν την ενεργοποίηση — επεξεργάσιμο εδώ, αποθηκεύεται
  // μαζί με την περίοδο ώστε να επιβιώνει από refresh.
  const [planTables, setPlanTables] = useState(prefRef.current.tables);
  const changePlanTables = (v) => {
    const n = Math.max(1, Math.min(300, Math.round(Number(v) || 0)));
    setPlanTables(n);
    try {
      localStorage.setItem("buzz_signup_pref",
        JSON.stringify({ ...prefRef.current, tables: n }));
      prefRef.current.tables = n;
    } catch {}
  };

  const load = useCallback(() => {
    axios.get(`${config.API_BASE_URL}/buzz/billing`).then(r => setB(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  // Πριν την ενεργοποίηση, η περίοδος ακολουθεί αυτόματα την επιλογή της
  // landing (ή το default ετήσια) — μία φορά, μετά αποφασίζει ο χρήστης.
  useEffect(() => {
    if (!b || appliedPrefRef.current) return;
    if ((b.status === "inactive" || b.status === "none") && b.billingPeriod !== prefRef.current.period) {
      appliedPrefRef.current = true;
      axios.put(`${config.API_BASE_URL}/buzz/billing/period`, { period: prefRef.current.period })
        .then(load).catch(() => {});
    } else {
      appliedPrefRef.current = true;
    }
  }, [b, load]);

  const startCard = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${config.API_BASE_URL}/buzz/billing/card/start`);
      window.location.href = data.checkoutUrl; // Viva Smart Checkout
    } finally { setBusy(false); }
  };

  const post = async (path) => {
    await axios.post(`${config.API_BASE_URL}${path}`);
    load();
  };

  const setPeriod = async (period) => {
    await axios.put(`${config.API_BASE_URL}/buzz/billing/period`, { period });
    load();
  };

  const setDocType = async (docType) => {
    await axios.put(`${config.API_BASE_URL}/buzz/billing/doc-type`, { docType });
    load();
  };

  if (!b) return <BuzzShell><div className="bz-empty">…</div></BuzzShell>;

  const price = b.billingPeriod === "yearly" ? b.nextChargeEur.yearly : b.nextChargeEur.monthly;
  const credit = Math.min(b.pendingCreditEur || 0, price);
  const netPrice = +(price - credit).toFixed(2);
  // Πριν την ενεργοποίηση χωρίς τραπέζια: εκτίμηση με βάση το πλάνο τραπεζιών
  const isInactive = b.status === "inactive" || b.status === "none";
  const projQuota = Math.max(b.minBillable, planTables);
  const projPrice = +(projQuota * b.unitPrices[b.billingPeriod]).toFixed(2);
  const nextChargeDate = b.status === "trial" ? b.trialEndsAt : b.renewalDate;
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("el-GR", { day: "numeric", month: "long", year: "numeric" }) : null;
  const statusLabel = {
    inactive: t("Not active"), trial: t("Trial"), active: t("Active"),
    grace: t("Payment issue"), none: t("Not active"),
  }[b.status] || b.status;

  return (
    <BuzzShell>
      {(b.status === "trial" || b.status === "active" || b.status === "grace") && nextChargeDate && (
        <div className="bz-card" style={{
          borderLeft: `4px solid ${b.autoRenew && b.hasCard ? "#059669" : "#dc2626"}`,
          display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap",
        }}>
          {b.autoRenew && b.hasCard ? (
            <>
              <span style={{ fontSize: 15 }}>💳 {t("Next charge")}:</span>
              <b style={{ fontSize: 22 }}>{netPrice.toFixed(2)} €</b>
              <span style={{ fontSize: 15 }}>{t("on")} <b>{fmtDate(nextChargeDate)}</b></span>
              <span className="bz-hint">({b.billableQuota} {t("tables")} × {b.unitPrices[b.billingPeriod].toFixed(2)} € — {b.billingPeriod === "yearly" ? t("Yearly") : t("Monthly")}{credit > 0 && <> − {credit.toFixed(2)} € {t("activation credit")}</>})</span>
            </>
          ) : (
            <span style={{ fontSize: 15 }}>
              ⚠ {t("No charge will happen")} — {!b.hasCard ? t("no card on file.") : t("auto-renewal is cancelled.")}{" "}
              {t("The service ends on")} <b>{fmtDate(nextChargeDate)}</b>.
            </span>
          )}
        </div>
      )}
      <div className="bz-card">
        <div className="bz-form-row"><label>{t("Status")}</label><b>{statusLabel}</b></div>
        {b.status === "trial" && b.trialEndsAt && (
          <div className="bz-form-row"><label>{t("Trial ends")}</label>
            <span>{new Date(b.trialEndsAt).toLocaleDateString("el-GR")}</span></div>
        )}
        {b.renewalDate && b.status !== "trial" && (
          <div className="bz-form-row"><label>{t("Next renewal")}</label>
            <span>{new Date(b.renewalDate).toLocaleDateString("el-GR")}</span></div>
        )}
        {isInactive && b.activeTables === 0 ? (
          <div className="bz-form-row"><label>{t("Tables")}</label>
            <input type="number" min={1} max={300} value={planTables}
              style={{ width: 90 }}
              onChange={e => changePlanTables(e.target.value)} />
            <span className="bz-hint"
              style={planTables < b.minBillable ? { color: "#dc2626", fontWeight: 700 } : undefined}>
              ({t("minimum billed")}: {b.minBillable} — {t("you'll set them up after activation")})
            </span>
          </div>
        ) : (
          <div className="bz-form-row"><label>{t("Active tables")}</label>
            <span>{b.activeTables} {b.activeTables < b.minBillable &&
              <span className="bz-hint">({t("minimum billed")}: {b.minBillable})</span>}</span></div>
        )}
        <div className="bz-form-row"><label>{t("Billing period")}</label>
          <span>
            <span style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
              {["monthly", "yearly"].map((p) => (
                <label key={p} style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", fontWeight: b.billingPeriod === p ? 700 : 400 }}>
                  <input type="radio" name="billingPeriod" value={p}
                    checked={b.billingPeriod === p} onChange={() => setPeriod(p)}
                    style={{ accentColor: "#d97706", width: 16, height: 16 }} />
                  {p === "monthly"
                    ? <>{t("Monthly")} — {b.unitPrices.monthly.toFixed(2)} €/{t("table")}</>
                    : <>{t("Yearly")} — {b.unitPrices.yearly.toFixed(2)} €/{t("table")} ({t("2 months free")})</>}
                </label>
              ))}
            </span>
            {(isInactive || b.status === "trial") && (
              <span className="bz-hint" style={{ display: "block", marginTop: 4 }}>
                {t("Applies after the 15-day free trial ends — you pay nothing during the trial.")}
              </span>
            )}
          </span></div>
        <div className="bz-form-row"><label>{t("Billing document")}</label>
          <span style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
            {["invoice", "receipt"].map((d) => (
              <label key={d} style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", fontWeight: b.docType === d ? 700 : 400 }}>
                <input type="radio" name="docType" value={d}
                  checked={b.docType === d} onChange={() => setDocType(d)}
                  style={{ accentColor: "#d97706", width: 16, height: 16 }} />
                {d === "invoice" ? t("Invoice") : t("Receipt")}
              </label>
            ))}
          </span></div>
        <div className="bz-form-row"><label>{t("Next charge")}</label>
          <span>
            <b>{(isInactive && b.activeTables === 0 ? projPrice : netPrice).toFixed(2)} €</b>{" "}
            / {b.billingPeriod === "yearly" ? t("year") : t("month")}{" "}
            <span className="bz-hint">({isInactive && b.activeTables === 0 ? projQuota : b.billableQuota} {t("tables")} × {b.unitPrices[b.billingPeriod].toFixed(2)} €{credit > 0 && <> − {credit.toFixed(2)} € {t("activation credit")}</>})</span>
            <span className="bz-hint" style={{ display: "block", marginTop: 4 }}>
              {isInactive
                ? <>{t("The first charge will happen once the free 15-day trial is over.")}{" "}{t("If you activate today, that's")} <b>{fmtDate(new Date(Date.now() + (b.trialDays || 15) * 86400000))}</b>.</>
                : b.status === "trial" && b.trialEndsAt
                  ? <>{t("First charge on")} <b>{fmtDate(b.trialEndsAt)}</b> ({t("when the trial ends")}).</>
                  : nextChargeDate
                    ? <>{t("Renews on")} <b>{fmtDate(nextChargeDate)}</b>.</>
                    : null}
              {" "}{b.docType === "receipt"
                ? t("We'll email you a reminder before every charge and a receipt after each renewal.")
                : t("We'll email you a reminder before every charge and an invoice after each renewal.")}
            </span>
          </span></div>
        <div className="bz-form-row"><label>{t("Card")}</label>
          <span>{b.hasCard ? `**** ${b.cardLast4 || ""}` : t("No card on file")}</span></div>
      </div>

      {(b.status === "inactive" || b.status === "none") && (
        <div className="bz-card" style={{ borderLeft: "4px solid #d97706" }}>
          <h3>🚀 {t("Start your 15-day free trial")}</h3>
          <p className="bz-hint">
            {t("To activate your card we make a one-time 1 € charge, which is credited to your first invoice. The first subscription charge happens when the trial ends — and if you cancel during the trial, the 1 € is refunded automatically.")}
          </p>
          <button className="bz-btn primary" disabled={busy} onClick={startCard}>
            {t("Add card & start trial")}
          </button>
          <p className="bz-hint" style={{ marginTop: 10 }}>
            🔒 {t("Secure payment via")} <b style={{ color: "#1a1f71" }}>viva.com</b> — {t("your card details are never stored on our systems.")}
          </p>
        </div>
      )}

      {!b.hasCard && b.status !== "inactive" && b.status !== "none" && (
        <div className="bz-card" style={{ borderLeft: "4px solid #d97706" }}>
          <h3>💳 {t("Add a card")}</h3>
          <p className="bz-hint">
            {t("To activate your card we make a one-time 1 € charge, which is credited to your first invoice. The first subscription charge happens when the trial ends — and if you cancel during the trial, the 1 € is refunded automatically.")}
          </p>
          <button className="bz-btn primary" disabled={busy} onClick={startCard}>
            {t("Add card")}
          </button>
          <p className="bz-hint" style={{ marginTop: 10 }}>
            🔒 {t("Secure payment via")} <b style={{ color: "#1a1f71" }}>viva.com</b> — {t("your card details are never stored on our systems.")}
          </p>
        </div>
      )}

      {b.hasCard && (
        <div className="bz-card">
          <div className="bz-toolbar">
            <button className="bz-btn" onClick={startCard} disabled={busy}>💳 {t("Change card")}</button>
            {b.autoRenew && b.status !== "inactive" ? (
              <button className="bz-btn danger" onClick={() => {
                if (window.confirm(b.status === "trial"
                  ? t("Cancel the trial? Your 1 € activation charge will be refunded automatically.")
                  : t("Cancel auto-renewal? Service continues until the period ends.")))
                  post("/buzz/billing/cancel");
              }}>{t("Cancel subscription")}</button>
            ) : (
              <button className="bz-btn primary" onClick={() => post("/buzz/billing/resume")}>
                {t("Resume auto-renewal")}
              </button>
            )}
          </div>
          {!b.autoRenew && (
            <p className="bz-hint">⚠ {t("Auto-renewal is off — the service will stop at the end of the current period.")}</p>
          )}
        </div>
      )}

      {b.history?.length > 0 && (
        <div className="bz-card">
          <h4 style={{ marginBottom: 8 }}>{t("Table quota changes")}</h4>
          <table className="bz-table-list">
            <thead><tr><th>{t("Date")}</th><th>{t("Tables")}</th><th>{t("Amount")}</th><th>{t("Status")}</th></tr></thead>
            <tbody>
              {b.history.map((h, i) => (
                <tr key={i}>
                  <td>{new Date(h.created_at).toLocaleDateString("el-GR")}</td>
                  <td>{h.old_quota} → {h.new_quota}</td>
                  <td>{Number(h.prorated_amount).toFixed(2)} €</td>
                  <td><span className="bz-chip">{h.payment_status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!b.vivaRecurringConfigured && (
        <p className="bz-hint">ℹ {t("Dev mode: recurring charges are simulated (Viva merchant credentials not set).")}</p>
      )}
    </BuzzShell>
  );
}
